'use client'

import Image from 'next/image'
import {FC} from 'react'

import {Section} from '@/components/common/content/Section'
import {Hero} from '@/components/common/hero/Hero'
import {Tile} from '@/components/common/tiles/Tile'
import {usePreferences} from '@/components/context/Preferences'
import {Page} from '@/components/Page'
import {DomainResolutionForm} from '@/components/service-request/domain-resolution/DomainResolutionForm'
import {HowServiceRequestWorks} from '@/components/service-request/HowServiceRequestWorks'
import {ServiceRequestBenefits} from '@/components/service-request/ServiceRequestBenefits'
import {ServiceRequestLayout} from '@/components/service-request/ServiceRequestLayout'
import {ServiceRequestTiles} from '@/components/service-request/ServiceRequestTiles'
import {getDomainResolutionContent} from '@/content/service-request/domainResolution'
import {useTranslation} from '@/i18n'
import {HERO_PADDING_DESKTOP, HERO_PADDING_MOBILE} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {MOBILE_SCREENSIZE, TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'
import {getServicePrices} from '@/utils/supported-services/services'

const DomainResolution: FC = () => {
  const {t} = useTranslation()

  const {currency} = usePreferences()
  const {hero, tiles, usp, howItWorksSteps} = getDomainResolutionContent()
  return (
    <>
      <Page>
        <Hero title={t(hero.title)} subtitle={t(hero.subtitle)} className="overflow-hidden">
          <div className="image">
            <Image
              src={hero.imageSrc}
              alt={hero.imageAlt}
              sizes={`${MOBILE_SCREENSIZE} 245px, ${TABLET_SCREENSIZE} 405px, 450px`}
              fill
              priority
              style={{objectFit: 'contain'}}
            />
          </div>
        </Hero>
        <Section hasPaddingTop>
          <ServiceRequestTiles title={t(tiles.title)}>
            {tiles.list.map(({key, title, description, imageSrc, imageAlt}) => (
              <Tile
                key={key}
                title={t(title)}
                description={t(description)}
                imageSrc={imageSrc}
                imageAlt={imageAlt}
                price={getServicePrices().DomainResolution[key][currency]}
                isFinalPrice={false}
              />
            ))}
          </ServiceRequestTiles>
        </Section>
        <Section hasPaddingTop hasPaddingBottom>
          <ServiceRequestLayout
            content={
              <>
                <Section hasContainer={false}>
                  <ServiceRequestBenefits
                    title={t(usp.title)}
                    subtitle={t(usp.threats.title)}
                    list={usp.threats.features}
                  />
                  <ServiceRequestBenefits
                    subtitle={t(usp.competition.title)}
                    list={usp.competition.features}
                  />
                </Section>
                <Section hasContainer={false} hasPaddingTop>
                  <HowServiceRequestWorks steps={howItWorksSteps} />
                </Section>
              </>
            }
            form={<DomainResolutionForm />}
          />
        </Section>
      </Page>

      <style jsx>{`
        .image {
          position: relative;
          width: 450px;
          height: 310px;
          margin-left: ${theme.spacing[5]};
          margin-right: -${theme.spacing[7]};
          margin-bottom: -${HERO_PADDING_DESKTOP};
        }

        @media ${TABLET_SCREENSIZE} {
          .image {
            width: 405px;
            height: 250px;
            margin-left: 0;
            margin-right: -${theme.spacing[6]};
            margin-top: ${theme.spacing[6]};
          }
        }

        @media ${MOBILE_SCREENSIZE} {
          .image {
            width: 245px;
            height: 150px;
            margin-right: -${theme.spacing[4]};
            margin-top: ${theme.spacing[4]};
            margin-bottom: -${HERO_PADDING_MOBILE};
          }
        }
      `}</style>
    </>
  )
}

export default DomainResolution
